import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AToggle } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Popover } from "@/components/admin/popover";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { Can } from "@/components/auth/can";
import { CreateLinkModal } from "@/components/admin/short-links/create-link-modal";
import { QrModal } from "@/components/admin/short-links/qr-modal";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePageTitle } from "@/hooks/use-page-title";
import { fmtN } from "@/lib/format";
import {
	CHANNEL_LABEL,
	KIND_LABEL,
	STATUS_LABEL,
	type Shortlink,
	type ShortlinkKind,
	type ShortlinkSource,
	type ShortlinkStatus,
	shortLinksApi,
} from "@/lib/short-links/short-links-api";
import {
	useArchiveShortLink,
	useInvalidateShortLinkCache,
	useShortLinkHealth,
	useShortLinksList,
	useShortLinksOverview,
	useUpdateShortLink,
} from "@/lib/short-links/short-links-hooks";

const PAGE_SIZE = 25;

const SOURCES: Array<{ value: ShortlinkSource | "all"; label: string }> = [
	{ value: "all", label: "Tous" },
	{ value: "system", label: "Système" },
	{ value: "manual", label: "Manuels" },
];

const STATUSES: ShortlinkStatus[] = ["active", "paused", "expired", "archived"];

const KINDS: ShortlinkKind[] = [
	"order_tracking",
	"order_payment",
	"order_invoice",
	"monde_tracking",
	"product",
	"category",
	"promotion",
	"campaign",
	"cms",
	"custom",
];

const relative = (iso: string | null): string => {
	if (!iso) return "—";
	const diff = Date.now() - new Date(iso).getTime();
	const minutes = Math.round(diff / 60_000);
	if (minutes < 1) return "à l'instant";
	if (minutes < 60) return `il y a ${minutes} min`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `il y a ${hours} h`;
	const days = Math.round(hours / 24);
	if (days < 31) return `il y a ${days} j`;
	return new Date(iso).toLocaleDateString("fr-FR");
};

export function AdminShortLinksPage() {
	usePageTitle("Liens courts");
	const navigate = useNavigate();
	const toast = useToast();
	const confirm = useConfirm();

	const [page, setPage] = useState(1);
	const [q, setQ] = useState("");
	const [source, setSource] = useState<ShortlinkSource | "all">("all");
	const [kind, setKind] = useState<ShortlinkKind | null>(null);
	const [status, setStatus] = useState<ShortlinkStatus | null>(null);
	const [creating, setCreating] = useState(false);
	const [qrLink, setQrLink] = useState<Shortlink | null>(null);

	const filter = useMemo(
		() => ({
			page,
			pageSize: PAGE_SIZE,
			q: q.trim() || undefined,
			source: source === "all" ? undefined : source,
			kind: kind ?? undefined,
			status: status ?? undefined,
		}),
		[page, q, source, kind, status],
	);

	const { data, isLoading } = useShortLinksList(filter);
	const { data: overview } = useShortLinksOverview(30);
	const { data: health } = useShortLinkHealth();
	const update = useUpdateShortLink();
	const archive = useArchiveShortLink();
	const invalidate = useInvalidateShortLinkCache();

	const rows = data?.data ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const copy = (text: string, message: string) => {
		void navigator.clipboard.writeText(text);
		toast.success(message);
	};

	const toggleStatus = async (link: Shortlink, on: boolean) => {
		try {
			await update.mutateAsync({ id: link.id, data: { status: on ? "active" : "paused" } });
			toast.success(on ? `Lien « ${link.code} » réactivé.` : `Lien « ${link.code} » mis en pause.`);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Changement de statut impossible."));
		}
	};

	const doArchive = async (link: Shortlink) => {
		const ok = await confirm({
			title: `Archiver le lien « ${link.code} » ?`,
			message:
				"Le lien cessera de rediriger. Les SMS et les documents déjà envoyés tomberont sur une page d'expiration. Le code reste réservé à vie : il ne sera jamais réattribué.",
			tone: "danger",
			confirmLabel: "Archiver",
			requireText: link.code,
			requireHint: "Saisissez le code pour confirmer.",
		});
		if (!ok) return;
		try {
			await archive.mutateAsync(link.id);
			toast.success(`Lien « ${link.code} » archivé.`);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Archivage impossible."));
		}
	};

	const doInvalidate = async (link: Shortlink) => {
		try {
			await invalidate.mutateAsync(link.id);
			toast.success("Cache purgé : la nouvelle destination s'applique immédiatement.");
		} catch (e) {
			toast.error(extractErrorMessage(e, "Purge impossible."));
		}
	};

	const totals = overview?.totals;

	return (
		<>
			<PageHead
				title="Liens courts"
				sub="Liens traçables pour SMS, WhatsApp et réseaux sociaux — destination modifiable après envoi"
				actions={
					<Can permission="MARKETING.CMS.WRITE">
						<AdminButton variant="primary" onClick={() => setCreating(true)}>
							<AdminIcon n="plus" s={14} /> Nouveau lien
						</AdminButton>
					</Can>
				}
			/>

			{health?.degraded ? (
				<AlertBanner
					variant="warning"
					title="Comptage des clics dégradé"
					description="La file d'enregistrement est indisponible : les redirections fonctionnent, mais le détail des clics (navigateur, pays, appareil) n'est pas enregistré. Seuls les volumes sont conservés."
				/>
			) : null}

			{health?.jobs && !health.jobs.enabled ? (
				<AlertBanner
					variant="info"
					title="Agrégation des statistiques désactivée"
					description="Les clics sont enregistrés mais ne sont pas encore agrégés sur cet environnement : les graphiques resteront vides."
				/>
			) : null}

			<div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
				<Kpi
					label="Liens actifs"
					value={fmtN(overview?.activeLinks ?? 0)}
					icon="zap"
					variant="forest"
				/>
				<Kpi
					label="Clics humains (30 j)"
					value={fmtN(totals?.humans ?? 0)}
					icon="trend"
					variant="accent"
					spark={(overview?.series ?? []).map((s) => s.humans)}
				/>
				<Kpi
					label="Visiteurs uniques (30 j)"
					value={fmtN(totals?.uniques ?? 0)}
					icon="users"
				/>
				<Kpi
					label="Robots et pré-visites"
					value={`${Math.round((totals?.nonHumanRate ?? 0) * 100)} %`}
					icon="shield"
					variant="warning"
					foot="aperçus WhatsApp et passerelles opérateur inclus"
				/>
			</div>

			<div className="toolbar" style={{ marginTop: 16 }}>
				<input
					className="aform-i"
					placeholder="Rechercher un code, un libellé, une campagne…"
					value={q}
					onChange={(e) => {
						setQ(e.target.value);
						setPage(1);
					}}
					style={{ minWidth: 260 }}
				/>
				<Segmented
					options={SOURCES}
					value={source}
					onChange={(v) => {
						setSource(v);
						setPage(1);
					}}
				/>
			</div>

			<div className="filter-bar" style={{ marginTop: 8 }}>
				<ChipFilter on={kind === null} onClick={() => setKind(null)}>
					Tous les types
				</ChipFilter>
				{KINDS.map((k) => (
					<ChipFilter
						key={k}
						on={kind === k}
						onClick={() => {
							setKind(kind === k ? null : k);
							setPage(1);
						}}
					>
						{KIND_LABEL[k]}
					</ChipFilter>
				))}
			</div>

			<div className="filter-bar" style={{ marginTop: 6, marginBottom: 14 }}>
				<ChipFilter on={status === null} onClick={() => setStatus(null)}>
					Tous les statuts
				</ChipFilter>
				{STATUSES.map((s) => (
					<ChipFilter
						key={s}
						on={status === s}
						onClick={() => {
							setStatus(status === s ? null : s);
							setPage(1);
						}}
					>
						{STATUS_LABEL[s]}
					</ChipFilter>
				))}
			</div>

			<div className="table-wrap">
				<table className="table">
					<thead>
						<tr>
							<th>Code</th>
							<th>Libellé</th>
							<th>Destination</th>
							<th>Canal</th>
							<th className="right">Clics</th>
							<th>Dernier clic</th>
							<th className="center">Actif</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={8} className="muted center" style={{ padding: 32 }}>
									Chargement…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={8}>
									<div className="empty">
										Aucun lien court. Créez-en un, ou laissez le système en
										générer pour les commandes.
									</div>
								</td>
							</tr>
						) : (
							rows.map((link) => (
								<tr key={link.id}>
									<td>
										<button
											type="button"
											className="mono"
											onClick={() => copy(link.smsForm, "Lien copié pour SMS.")}
											title="Copier pour SMS"
											style={{
												background: "none",
												border: "none",
												padding: 0,
												cursor: "pointer",
												color: "var(--a-terracotta)",
												fontWeight: 600,
											}}
										>
											{link.smsForm}
										</button>
									</td>
									<td>
										{link.label ?? <span className="muted">—</span>}
										{link.source === "system" ? (
											<div className="muted" style={{ fontSize: 11 }}>
												automatique
											</div>
										) : null}
									</td>
									<td>
										<Tag variant="neutral" noDot>
											{KIND_LABEL[link.kind]}
										</Tag>{" "}
										{link.targetRedacted ? (
											<span
												className="muted mono"
												title="Masquée : la destination d'un lien système contient un jeton d'accès à la commande."
											>
												•••
											</span>
										) : (
											<span
												className="muted"
												title={link.targetUrl ?? ""}
												style={{ fontSize: 12 }}
											>
												{(link.targetUrl ?? "").replace(/^https?:\/\//, "").slice(0, 42)}
												{(link.targetUrl ?? "").length > 49 ? "…" : ""}
											</span>
										)}
									</td>
									<td>
										{link.channel ? (
											<Tag variant="neutral" noDot>
												{CHANNEL_LABEL[link.channel]}
											</Tag>
										) : (
											<span className="muted">—</span>
										)}
									</td>
									<td className="right">
										<div className="tn" style={{ fontWeight: 700 }}>
											{fmtN(link.clicksHuman)}
										</div>
										{link.clicksTotal > link.clicksHuman ? (
											<div className="muted tn" style={{ fontSize: 11 }}>
												+{fmtN(link.clicksTotal - link.clicksHuman)} robots
											</div>
										) : null}
									</td>
									<td className="muted">{relative(link.lastClickAt)}</td>
									<td className="center">
										<Can
											permission="MARKETING.CMS.WRITE"
											fallback={
												<Tag
													variant={link.status === "active" ? "success" : "neutral"}
													noDot
												>
													{STATUS_LABEL[link.status]}
												</Tag>
											}
										>
											{/* `on=` contrôlé : sans cette prop, le toggle garde
											    l'état de la première ligne rendue et ne se met
											    plus à jour d'une ligne à l'autre. */}
											<AToggle
												on={link.status === "active"}
												onChange={(v) => void toggleStatus(link, v)}
											/>
										</Can>
									</td>
									<td>
										<Popover
											align="right"
											width={210}
											trigger={
												<button type="button" className="btn btn-ghost btn-icon">
													<AdminIcon n="dots" s={16} />
												</button>
											}
										>
											{(close) => (
												<div className="dd-menu">
													<button
														type="button"
														className="dd-item"
														onClick={() => {
															copy(link.smsForm, "Lien copié pour SMS.");
															close();
														}}
													>
														Copier pour SMS
													</button>
													<button
														type="button"
														className="dd-item"
														onClick={() => {
															copy(link.shortUrl, "Lien complet copié.");
															close();
														}}
													>
														Copier le lien complet
													</button>
													<button
														type="button"
														className="dd-item"
														onClick={() => {
															navigate(`/liens-courts/${link.id}`);
															close();
														}}
													>
														Statistiques
													</button>
													{link.source === "manual" ? (
														<button
															type="button"
															className="dd-item"
															onClick={() => {
																setQrLink(link);
																close();
															}}
														>
															QR code
														</button>
													) : null}
													<button
														type="button"
														className="dd-item"
														onClick={() => {
															void shortLinksApi.exportCsv(link.id, link.code);
															close();
														}}
													>
														Exporter les clics
													</button>
													<Can permission="MARKETING.CMS.WRITE">
														<button
															type="button"
															className="dd-item"
															onClick={() => {
																void doInvalidate(link);
																close();
															}}
														>
															Purger le cache
														</button>
														<button
															type="button"
															className="dd-item"
															style={{ color: "var(--a-danger)" }}
															onClick={() => {
																void doArchive(link);
																close();
															}}
														>
															Archiver
														</button>
													</Can>
												</div>
											)}
										</Popover>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{total > PAGE_SIZE ? (
				<Pagination
					from={(page - 1) * PAGE_SIZE + 1}
					to={Math.min(page * PAGE_SIZE, total)}
					total={total}
					current={page}
					totalPages={totalPages}
					onChange={setPage}
				/>
			) : null}

			{creating ? <CreateLinkModal onClose={() => setCreating(false)} /> : null}
			{qrLink ? <QrModal link={qrLink} onClose={() => setQrLink(null)} /> : null}
		</>
	);
}
